import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { cn } from "@/lib/utils";
import React from "react";
import type { FilterOperator } from "../types";
import { useFormulaScopes, type FormulaContext } from "./api";
import { FormulaEditor } from "./formula-editor";
import { FormulaText } from "./formula-text";

/** The ƒx glyph of formulas (a serif italic f, as in spreadsheets). */
export function FxGlyph({ className }: { className?: string }) {
    return (
        <span className={cn("font-serif text-sm font-semibold italic leading-none", className)} aria-hidden>
            ƒx
        </span>
    );
}

/** The button at the end of a condition: compare with a formula, or back to a typed value. */
export function FxButton({ active, onClick }: { active: boolean; onClick: () => void }) {
    const label = active ? "Back to a manual value" : "Use a formula";
    return (
        <SimpleTooltip message={label} asChild>
            <button
                type="button"
                aria-label={label}
                aria-pressed={active}
                onClick={onClick}
                className={cn(
                    "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded border text-gray-600 hover:border-[#c7cfee] hover:bg-[#eef0fa] hover:text-[#3346a0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active ? "border-[#93a3db] bg-[#eef0fa] text-[#3346a0]" : "border-transparent"
                )}
            >
                <FxGlyph />
            </button>
        </SimpleTooltip>
    );
}

/** A condition's formula, closed: its text coloured on one line. A click opens the editor. */
export function FormulaPill({ context, label, invalid, onClick }: { context: FormulaContext; label: string; invalid?: boolean; onClick: () => void }) {
    const scopes = useFormulaScopes(context)?.map(item => item.scope) ?? [];
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={`Edit the formula ${label}`}
            title={label}
            className={cn(
                "inline-flex h-8 min-w-0 max-w-[26rem] items-center gap-2 rounded-sm border bg-[#f5f6fd] px-2 text-left shadow-sm hover:border-[#4d62c0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                invalid ? "border-red-500" : "border-[#93a3db]"
            )}
        >
            <FxGlyph className="text-[#3346a0]" />
            <span className="min-w-0 truncate font-mono text-[13px] leading-5">
                <FormulaText text={label} scopes={scopes} />
            </span>
        </button>
    );
}

/**
 * The formula editor of a condition, in a popover anchored to the condition's row (never a
 * dialog: the advanced filters already are one).
 */
export function ConditionFormulaPopover({
    open,
    onOpenChange,
    anchor,
    context,
    field,
    fieldLabel,
    fieldType,
    operator,
    formula,
    formulaLabel,
    onDone,
    onManual
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    anchor: React.ReactNode;
    context: FormulaContext;
    field: string;
    fieldLabel: string;
    fieldType?: string;
    operator: FilterOperator;
    formula?: string | null;
    formulaLabel?: string | null;
    onDone: (formula: { canonical: string; display: string }) => void;
    onManual: () => void;
}) {
    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverAnchor asChild>{anchor}</PopoverAnchor>
            <PopoverContent
                align="start"
                className="w-[640px] max-w-[calc(100vw-2rem)] p-0"
                onOpenAutoFocus={event => event.preventDefault()}
                onEscapeKeyDown={event => {
                    // Escape first closes the suggestions under the cursor.
                    if ((document.activeElement as HTMLElement | null)?.dataset.completionOpen) event.preventDefault();
                }}
                onInteractOutside={event => {
                    // The Insert menus open in popovers of their own: clicking them is not leaving.
                    const target = event.target as HTMLElement | null;
                    if (target?.closest("[data-radix-popper-content-wrapper]")) event.preventDefault();
                }}
            >
                {open && (
                    <FormulaEditor
                        context={context}
                        field={field}
                        fieldLabel={fieldLabel}
                        fieldType={fieldType}
                        operator={operator}
                        formula={formula}
                        formulaLabel={formulaLabel}
                        onDone={formula => {
                            onDone(formula);
                            onOpenChange(false);
                        }}
                        onCancel={() => onOpenChange(false)}
                        onManual={() => {
                            onManual();
                            onOpenChange(false);
                        }}
                    />
                )}
            </PopoverContent>
        </Popover>
    );
}
